import React, { useState } from "react";
import { Card, CardContent } from "../ui/card";
import { FACTORS, FactorList } from "./Factors";
import { Overline, StockHeader, ThesisStrip } from "./Shared";
import { LEDGER, QUARTERS, STOCK, fmtPct } from "./data";

const MARK = {
  N: { t: "NEW", c: "bg-emerald-500 text-black" }, A: { t: "ADD", c: "bg-emerald-500/40 text-emerald-200" },
  H: { t: "HOLD", c: "bg-zinc-700 text-zinc-300" }, T: { t: "TRIM", c: "bg-amber-500/40 text-amber-200" },
  X: { t: "EXIT", c: "bg-red-500/70 text-white" },
};
const FILTERS = [["all", "All"], ["cohort", "New cohort"], ["elite", "Skill ≥ 80th"], ["sellers", "Trims & exits"]];

const pick = (f) => LEDGER.filter((r) => f === "all" ? true : f === "cohort" ? !r.older : f === "elite" ? r.skill >= 80
  : Object.values(r.q).some((a) => a === "T" || a === "X"));

export default function VariantLedger() {
  const [flt, setFlt] = useState("all");
  const [fac, setFac] = useState("f1");
  const rows = pick(flt);
  const f = FACTORS.find((x) => x.id === fac);
  return (
    <div className="space-y-4" data-testid="kfm-variant-c">
      <StockHeader compact />
      <ThesisStrip />
      <div className="grid grid-cols-1 2xl:grid-cols-4 gap-4">
        <div className="2xl:col-span-1 space-y-2">
          <Overline>Seven factors</Overline>
          <FactorList prefix="kfm-c" active={fac} onPick={setFac} />
        </div>
        <div className="2xl:col-span-3 space-y-4">
          <Card data-testid="kfm-variant-c-hero"><CardContent className="pt-5 pb-4">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div>
                <Overline>Smart-money ledger — who is in, when, at what estimated cost</Overline>
                <div className="text-[11px] text-muted-foreground mt-1">13F disclosures · est. cost = average price in the quarter of each purchase · skill = 12m sector hit-rate percentile</div>
              </div>
              <div className="flex gap-1.5">
                {FILTERS.map(([k, l]) => (
                  <button key={k} onClick={() => setFlt(k)} data-testid={`kfm-ledger-filter-${k}`}
                    className={`px-2.5 py-1 rounded text-[11px] border ${flt === k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="kfm-smart-money-ledger-table">
                <thead className="border-b border-border text-muted-foreground">
                  <tr>
                    <th className="py-1.5 px-2 text-left">Fund</th><th className="py-1.5 px-2 text-right">Skill</th><th className="py-1.5 px-2 text-right">Hit 12m</th>
                    {QUARTERS.map((q) => <th key={q} className="py-1.5 px-2 text-center font-mono">{q}</th>)}
                    <th className="py-1.5 px-2 text-right">Est. cost</th><th className="py-1.5 px-2 text-right">vs cost</th><th className="py-1.5 px-2 text-right">Position</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const pnl = r.cost ? (STOCK.price / r.cost - 1) * 100 : null;
                    return (
                      <tr key={r.fund} className="border-b border-border/40 hover:bg-muted/30" data-testid="kfm-ledger-fund-row">
                        <td className="py-2 px-2"><span className="font-medium">{r.fund}</span> <span className="text-[10px] text-muted-foreground">{r.type}</span></td>
                        <td className={`py-2 px-2 text-right font-mono ${r.skill >= 80 ? "text-emerald-400 font-bold" : ""}`}>{r.skill}th</td>
                        <td className="py-2 px-2 text-right font-mono">{r.hit}%</td>
                        {QUARTERS.map((q) => (
                          <td key={q} className="py-2 px-2 text-center">
                            {r.q[q] ? <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${MARK[r.q[q]].c}`}>{MARK[r.q[q]].t}</span> : <span className="text-muted-foreground/40">·</span>}
                          </td>
                        ))}
                        <td className="py-2 px-2 text-right font-mono">{r.cost ? `$${r.cost.toFixed(2)}` : "—"}</td>
                        <td className={`py-2 px-2 text-right font-mono ${pnl == null ? "" : pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>{pnl == null ? "—" : fmtPct(pnl)}</td>
                        <td className="py-2 px-2 text-right font-mono text-muted-foreground">{r.pos}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-3 border-t border-border/50 text-[11px]">
              <div><Overline>New cohort</Overline><span className="font-mono text-sm">9 funds · $1.15B</span></div>
              <div><Overline>Cohort in profit</Overline><span className="font-mono text-sm text-emerald-400">9 / 9</span></div>
              <div><Overline>Skilled sellers</Overline><span className="font-mono text-sm">0 (≥80th)</span></div>
              <div><Overline>Older holders underwater</Overline><span className="font-mono text-sm text-amber-400">2 (Whale Rock, T. Rowe)</span></div>
            </div>
          </CardContent></Card>
          <Card data-testid="kfm-c-factor-detail"><CardContent className="pt-4 pb-4">
            <div className="text-xs font-bold tracking-[0.15em] mb-2">{f.name} <span className="font-normal tracking-normal text-muted-foreground">— {f.def}</span></div>
            {f.detail}
          </CardContent></Card>
        </div>
      </div>
    </div>
  );
}
